/**
 * LeftNav — collapsible brand-purple vertical navigation rail.
 *
 * Replaces the former top NavBar. Logo at top, icon+label nav items, account
 * avatar at the bottom. Brand purple (#2E1065) bar; the active item uses
 * #4C1D95 per the design system (brand is reserved for navigation & identity).
 */
import { useEffect, useState, type CSSProperties } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Bell, ChevronDown, ChevronRight } from 'lucide-react';
import { colors, radius } from '../theme';
import { useAuth } from '../auth/useAuth';
import { navItemsForView } from './roleViews';
import type { NavItem } from './nav';

/** A single leaf nav row (icon + label). */
function NavRow({ item, collapsed, nested }: { item: NavItem; collapsed: boolean; nested?: boolean }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      title={item.label}
      style={({ isActive }) => ({
        ...navItem,
        ...(collapsed ? navItemCollapsed : {}),
        ...(nested ? navItemNested : {}),
        ...(isActive ? navItemActive : {}),
      })}
    >
      <span style={icon} aria-hidden>
        <Icon size={18} strokeWidth={2} />
      </span>
      {!collapsed && <span style={itemLabel}>{item.label}</span>}
    </NavLink>
  );
}

/** A parent nav item with an expandable list of sub-pages. */
function NavGroup({ item, collapsed, currentPath }: { item: NavItem; collapsed: boolean; currentPath: string }) {
  const Icon = item.icon;
  const childActive = item.children!.some((c) => currentPath.startsWith(c.to));
  const [open, setOpen] = useState(childActive);
  // Auto-expand when navigating into one of the sub-pages.
  useEffect(() => {
    if (childActive) setOpen(true);
  }, [childActive]);

  // Collapsed rail: render the parent as a plain icon link, no children/chevron.
  if (collapsed) {
    return <NavRow item={item} collapsed={collapsed} />;
  }

  return (
    <div>
      <div style={groupRow}>
        <NavLink
          to={item.to}
          title={item.label}
          style={({ isActive }) => ({ ...navItem, flex: 1, ...(isActive ? navItemActive : {}) })}
        >
          <span style={icon} aria-hidden>
            <Icon size={18} strokeWidth={2} />
          </span>
          <span style={itemLabel}>{item.label}</span>
        </NavLink>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          style={chevronBtn}
          aria-label={open ? `Collapse ${item.label}` : `Expand ${item.label}`}
          aria-expanded={open}
        >
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>
      </div>
      {open &&
        item.children!.map((child) => (
          <NavRow key={child.to} item={child} collapsed={collapsed} nested />
        ))}
    </div>
  );
}

export function LeftNav() {
  const { user, activeView } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const width = collapsed ? 64 : 224;
  // Role-first scoping: trim the rail to the active view's surfaces (the `all`
  // view returns the full list).
  const navItems = navItemsForView(activeView);

  return (
    <nav style={{ ...rail, width }} aria-label="Primary">
      <div style={topBlock}>
        <div style={logoRow}>
          <span style={logoMark}>E2</span>
          {!collapsed && <span style={logoText}>Enable 2.0</span>}
        </div>
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          style={collapseBtn}
          aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
          title={collapsed ? 'Expand' : 'Collapse'}
        >
          {collapsed ? '»' : '«'}
        </button>
      </div>

      <div style={items}>
        {navItems.map((item) =>
          item.children && item.children.length > 0 ? (
            <NavGroup key={item.to} item={item} collapsed={collapsed} currentPath={location.pathname} />
          ) : (
            <NavRow key={item.to} item={item} collapsed={collapsed} />
          ),
        )}
      </div>

      <div style={{ ...account, ...(collapsed ? accountCollapsed : {}) }}>
        <button
          type="button"
          onClick={() => navigate('/notifications')}
          style={bellBtn}
          title="Notifications"
          aria-label="Notifications"
        >
          <Bell size={18} strokeWidth={2} />
          <span style={bellDot} aria-hidden />
        </button>
        <div style={avatar} title={user?.name ?? 'User'}>
          {user?.name?.charAt(0)?.toUpperCase() ?? 'U'}
        </div>
        {!collapsed && (
          <div style={accountText}>
            <div style={accountName}>{user?.name ?? 'User'}</div>
            <div style={accountEmail}>{user?.email ?? ''}</div>
          </div>
        )}
      </div>
    </nav>
  );
}

const rail: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  background: colors.brand,
  color: '#FFFFFF',
  flexShrink: 0,
  transition: 'width 0.15s ease',
  position: 'sticky',
  top: 0,
  height: '100vh',
  overflow: 'hidden',
};
const topBlock: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px 14px',
  gap: 8,
};
const logoRow: CSSProperties = { display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 };
const logoMark: CSSProperties = {
  width: 30,
  height: 30,
  borderRadius: radius.sm,
  background: colors.brandAccent,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 800,
  fontSize: '0.85rem',
  flexShrink: 0,
};
const logoText: CSSProperties = { fontWeight: 700, fontSize: '1rem', whiteSpace: 'nowrap' };
const collapseBtn: CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: 'rgba(255,255,255,0.6)',
  cursor: 'pointer',
  fontSize: '0.9rem',
  padding: 4,
  flexShrink: 0,
};
const items: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 2, padding: '8px 10px', flex: 1 };
const navItem: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '9px 12px',
  borderRadius: radius.sm,
  color: 'rgba(255,255,255,0.78)',
  textDecoration: 'none',
  fontSize: '0.92rem',
  fontWeight: 500,
  whiteSpace: 'nowrap',
};
const navItemCollapsed: CSSProperties = { justifyContent: 'center', padding: '9px 0' };
const navItemNested: CSSProperties = { paddingLeft: 34, fontSize: '0.88rem' };
const navItemActive: CSSProperties = {
  background: colors.brandActive,
  color: '#FFFFFF',
  fontWeight: 600,
};
const icon: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 18,
  height: 18,
  flexShrink: 0,
};
const itemLabel: CSSProperties = { overflow: 'hidden', textOverflow: 'ellipsis' };
const groupRow: CSSProperties = { display: 'flex', alignItems: 'center', gap: 2 };
const chevronBtn: CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: 'rgba(255,255,255,0.6)',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  padding: 6,
  flexShrink: 0,
};
const account: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '14px',
  borderTop: '1px solid rgba(255,255,255,0.12)',
};
const accountCollapsed: CSSProperties = { flexDirection: 'column', gap: 8, padding: '14px 0' };
const bellBtn: CSSProperties = {
  position: 'relative',
  background: 'transparent',
  border: 'none',
  color: 'rgba(255,255,255,0.85)',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 4,
  flexShrink: 0,
};
const bellDot: CSSProperties = {
  position: 'absolute',
  top: 2,
  right: 2,
  width: 7,
  height: 7,
  borderRadius: '50%',
  background: colors.brandAccent,
  border: `1.5px solid ${colors.brand}`,
};
const avatar: CSSProperties = {
  width: 32,
  height: 32,
  borderRadius: '50%',
  background: colors.brandAccent,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 700,
  fontSize: '0.85rem',
  flexShrink: 0,
};
const accountText: CSSProperties = { minWidth: 0 };
const accountName: CSSProperties = {
  fontSize: '0.85rem',
  fontWeight: 600,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};
const accountEmail: CSSProperties = {
  fontSize: '0.72rem',
  color: 'rgba(255,255,255,0.55)',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};
